import React, { MouseEventHandler } from 'react';

import clsx from 'clsx';
import Link from 'next/link';

import css from './index.module.css';

export type ButtonTheme = 'solid' | 'outline' | 'link' | 'clean';
export type ButtonSize = 'icon' | 'small' | 'large' | 'wide';
export type ButtonColor = 'black' | 'white' | 'grey' | 'orangeOnBlack' | 'lightBlueOnBlack';

export type ButtonProps = {
  dataTestId?: string;
  id?: string;
  children?: React.ReactNode;
  onClick?: () => void;
  color?: ButtonColor;
  disabled?: boolean;
  label?: string;
  newTab?: boolean;
  size?: ButtonSize;
  theme?: ButtonTheme;
  type?: 'submit' | 'button' | 'reset';
  url?: string;
};

const Button = ({
  id,
  dataTestId,
  children,
  onClick,
  color,
  disabled,
  label,
  newTab,
  size,
  theme = 'solid',
  type = 'button',
  url,
}: ButtonProps) => {
  const SizeClass = size ? size : ['solid', 'outline', 'wide'].indexOf(theme) >= 0 ? 'regular' : '';

  const classStack = clsx(css.element, css[theme], css[color || ''], css[SizeClass], {
    [css['is-disabled']]: disabled,
    'pointer-events-none': disabled,
  });
  const isJS = url ? /^javascript/.test(url) : undefined;

  const handleJSClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (!url || disabled) {
      return;
    }

    event.preventDefault();

    const trimmedFunction = url.replace('javascript:', '');

    const callback = new Function(`return ${trimmedFunction}`);

    callback();
  };

  return url ? (
    <Link
      href={isJS ? '#' : url}
      className={classStack}
      target={newTab ? '_blank' : undefined}
      onClick={isJS ? handleJSClick : undefined}
    >
      {children || label}
    </Link>
  ) : (
    <button
      id={id}
      data-testid={dataTestId}
      className={classStack}
      disabled={disabled}
      type={type}
      onClick={onClick}
    >
      {children || label}
    </button>
  );
};

export default Button;
